import { store$ } from '@redux/store';
import getHousingProductSnippetContentRequest$ from 'api/getHousingProductSnippetContent';
import LoadingStatus from 'enums/loadingStatus';
import { selectSnippetCommand } from 'features/searchResultsFacets';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

const popularSearches = document.querySelector('[data-content-popular-searches]');
const marketSnapshots = document.querySelector('[data-content-market-snapshots]');
const marketComplementary = document.querySelector('[data-content-market-complementary]');
const marketComplementaryContainer = 
  marketComplementary?.querySelector('[data-content-market-complementary-container]');
const marketSnapshotsContainer = marketSnapshots?.querySelector('[data-market-snapshots-container]');
const marketSnapshotsTitle = marketSnapshots?.querySelector('[data-market-snapshots-title]');

/**
 * Show the popular search section in the document
 */
const showPopularSearches = () => {
  popularSearches.removeAttribute('hidden');
};

/**
 * Show the market snapshot section in the document
 */
const showMarketSnapshot = () => {
  marketSnapshots.removeAttribute('hidden');
};

/**
 * Show the market complementary section in the document
 */
const showMarketComplementary = () => {
  marketComplementary.removeAttribute('hidden');
};

/**
 * Hide the popular search section in the document
 */
const hidePopularSeaches = () => {
  popularSearches.setAttribute('hidden', '');
};

/**
 * Hide the market snapshot section in the document
 */
const hideMarketSnapshot = () => {
  marketSnapshots.setAttribute('hidden', '');
};

/**
 * Show the market complementary section in the document
 */
const hideMarketComplementary = () => {
  marketComplementary.setAttribute('hidden', '');
};

const setPopularSearches = (data) => {
  popularSearches.innerHTML = data ?? ``;
};

/**
 * This function takes an array of objects representing the market snapshot snippets,
 * where each object has a property called Content.
 *
 * Then it takes the Content property of each object and joins them together into one string,
 * and sets the innerHTML of the marketSnapshotsContainer to the combined string.
 *
 * @param data - an array of snippets objects.
 */
const setMarketSnapshot = (data) => {
  const htmlSnapshots = data?.map((snapShot) => snapShot.Content || '');
  const combinedHtml = htmlSnapshots?.join('');
  marketSnapshotsContainer.innerHTML = combinedHtml ?? '';

  if (!combinedHtml || combinedHtml?.length === 0) {
    marketSnapshotsTitle.setAttribute('hidden', '');
  }else{
    marketSnapshotsTitle.removeAttribute('hidden');
  }
};

/**
 * This function takes an array of objects representing the market complementary snippets,
 * where each object has a property called Content.
 *
 * Then it takes the Content property of each object and joins them together into one string,
 * and sets the innerHTML of the marketComplementaryContainer to the combined string.
 *
 * @param data - an array of snippets objects.
 */
const setMarketComplementary = (data) => {
  const htmlSnippets = data?.map((snippet) => snippet.Content || '');
  const combinedHtml = htmlSnippets?.join('');
  marketComplementaryContainer.innerHTML = combinedHtml ?? '';

  if (!combinedHtml || combinedHtml?.length === 0) {
    marketComplementary.setAttribute('hidden', '');
  }else{
    marketComplementary.removeAttribute('hidden');
  }
};

/**
 * If there is no snippet information or the listing count is 0, hide the market complementary
 * snippet. Otherwise, show the market complementary snippet.
 *
 * @param listingCounts - This is the number of listings that are returned from the search.
 * @param snippetInformation - This is an object that contains the information for the snippet.
 */
const toggleMarketComplementarySnippet = (listingCounts, snippetInformation) => {
  if (!snippetInformation?.length || listingCounts === 0) {
    hideMarketComplementary();
  } else {
    showMarketComplementary();
  }
};

/**
 * If there is no snippet information or the listing count is 0, hide the market snapshot snippet.
 * Otherwise, show the market snapshot snippet.
 *
 * @param listingCounts - This is the number of listings that are returned from the search.
 * @param snippetInformation - This is an object that contains the information for the snippet.
 */
const toggleMarketSnapshotSnippet = (listingCounts, snippetInformation) => {
  if (!snippetInformation?.length || listingCounts === 0) {
    hideMarketSnapshot();
  } else {
    showMarketSnapshot();
  }
};

/**
 * If there is no snippet information or the listing count is 0, hide the popular searches
 * snippet. Otherwise, show the popular searches snippet.
 *
 * @param listingCounts - This is the number of listings that are returned from the search.
 * @param snippetInformation - This is an object that contains the information for the snippet.
 */
const togglePopularSearchesSnippet = (listingCounts, snippetInformation) => {
  if (!snippetInformation || listingCounts === 0) {
    hidePopularSeaches();
  } else {
    showPopularSearches();
  }
};

/*
 * This is a stream that is subscribing to the store and checking if the state has changed.
 */
const onResultsReady$ = store$.pipe(
  map(selectSnippetCommand),
  distinctUntilChanged(),
  debounceTime(100),
  switchMap((snippetCommand) => getHousingProductSnippetContentRequest$(snippetCommand).pipe(
    catchError(() => of(null)) // catch and ignore any errors
)),
  take(1));

/*
 * Returns the lastest listing count value whether the result count request is Succeeded when the
 * 'fetchResultCountsStatus' is triggered and its value is not undefined regardless of 
 * it is greater or smaller since the snippets depends on that to be re-generated.
 */
const onListingCount$ = store$.pipe(
  map((state) => ({
    fetchResultCountsStatus: state?.searchParameters?.fetchResultCountsStatus,
    total: state?.searchParameters?.counts?.listingCount,
  })),
  distinctUntilKeyChanged('fetchResultCountsStatus'),
  filter(
    ({fetchResultCountsStatus, total}) =>
      fetchResultCountsStatus === LoadingStatus.Succeeded &&
      total !== undefined
  ),
);

/*
 * When we apply some filter or navigate in the map several times the listing count process is cancelled
 * to avoid doing several request when it has been changed, we linked the onResultsReady$ only when 
 * the onListingCount$ changed. It can help us to control the request that causes delay to display the
 * snippets.
 * 
 * Then, destructure the array into two variables, one for the listing count and one for the response of the fetch
 * for the snippet information.
 *
 * Set the snippet information into the document and then call the toggle functions to show or hide the snippets.
 */
onListingCount$
  .pipe(
    switchMap(
      (listingCount) => onResultsReady$
                          .pipe(
                            map(response => ([listingCount, response])))),
    tap(([listingCount, response]) => {
      const marketComplementarySnippet = response?.MarketComplementarySnippets;
      const marketSnapshotSnippets = response?.MarketSnapshotSnippets;
      const popularSearchesSnippet = response?.PopularSearchesSnippet?.Content;
      const totalListingCount = listingCount?.total;

      setMarketComplementary(marketComplementarySnippet);
      setMarketSnapshot(marketSnapshotSnippets);
      setPopularSearches(popularSearchesSnippet);
      toggleMarketComplementarySnippet(totalListingCount, marketComplementarySnippet);
      toggleMarketSnapshotSnippet(totalListingCount, marketSnapshotSnippets);
      togglePopularSearchesSnippet(totalListingCount, popularSearchesSnippet);
    })
  )
  .subscribe();
